// lib/firebase.ts
import { getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type User as FirebaseAuthUser,
  type ConfirmationResult,
  Auth
} from "firebase/auth";
import { fetchFirebaseConfig } from "@/api/config";
import api from "@/api/axios";

// ---- Singleton init with cached config fetch ----
let appPromise: Promise<FirebaseApp> | null = null;
let authPromise: Promise<Auth> | null = null;

async function initApp(): Promise<FirebaseApp> {
  if (getApps().length) return getApps()[0];
  if (!appPromise) {
    appPromise = (async () => {
      const cfg = await fetchFirebaseConfig();     // must return valid Firebase config
      return initializeApp(cfg);
    })();
  }
  return appPromise;
}

// Export a single auth instance; ensure app is initialized first
// Consumers can import { auth } safely.
const app = await initApp();
export const auth = getAuth(app);

// ---- ReCAPTCHA + Phone Auth helpers (v2 invisible) ----
const recaptchaVerifiers = new Map<string, RecaptchaVerifier>();
let globalConfirmationResult: ConfirmationResult | null = null;

export const setGlobalConfirmationResult = (cr: ConfirmationResult) => { globalConfirmationResult = cr; };
export const getGlobalConfirmationResult = () => globalConfirmationResult;
export const clearGlobalConfirmationResult = () => { globalConfirmationResult = null; };

export const initializeRecaptcha = async (
  containerId: string,
  onSolved?: () => void
): Promise<RecaptchaVerifier> => {
  // Remove any existing container to avoid “already rendered” issues
  const existing = document.getElementById(containerId);
  if (existing) existing.remove();

  const container = document.createElement("div");
  container.id = containerId;
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  container.style.width = "1px";
  container.style.height = "1px";
  container.style.overflow = "hidden";
  container.style.opacity = "0";
  container.style.pointerEvents = "none";
  document.body.appendChild(container);

  // Clear previous verifier (if any)
  const prev = recaptchaVerifiers.get(containerId);
  if (prev) {
    try { prev.clear(); } catch {}
    recaptchaVerifiers.delete(containerId);
  }

  await new Promise(r => setTimeout(r, 50)); // let DOM settle

  // Firebase Phone Auth uses reCAPTCHA v2 (invisible) under the hood
  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: () => { onSolved?.(); },
    "expired-callback": () => { /* no-op */ },
  });

  await verifier.render();
  recaptchaVerifiers.set(containerId, verifier);
  return verifier;
};

export const setupPhoneAuth = async (
  phoneNumber: string,
  containerId: string,
  onSolved?: () => void
): Promise<ConfirmationResult> => {
  let verifier = recaptchaVerifiers.get(containerId);
  if (!verifier) verifier = await initializeRecaptcha(containerId, onSolved);
  return signInWithPhoneNumber(auth, phoneNumber, verifier);
};

export const verifyPhoneOTP = async (confirmationResult: ConfirmationResult, code: string) => {
  const result = await confirmationResult.confirm(code);
  const token = await result.user.getIdToken(/* forceRefresh? false */);
  return { user: result.user, token };
};

export const cleanupRecaptcha = (containerId: string) => {
  const v = recaptchaVerifiers.get(containerId);
  if (v) {
    try { v.clear(); } catch {}
    recaptchaVerifiers.delete(containerId);
  }
  const container = document.getElementById(containerId);
  if (container) container.remove();

  // Clean any stray elements Firebase may have added
  document.querySelectorAll('iframe[src*="recaptcha"], .grecaptcha-badge').forEach(el => {
    el.parentElement?.removeChild(el);
  });
};

export const cleanupAllRecaptcha = () => {
  for (const v of recaptchaVerifiers.values()) {
    try { v.clear(); } catch {}
  }
  recaptchaVerifiers.clear();
  document.querySelectorAll('#recaptcha-container-mobile, #recaptcha-container-desktop').forEach(el => el.remove());
  document.querySelectorAll('iframe[src*="recaptcha"], .grecaptcha-badge').forEach(el => el.parentElement?.removeChild(el));
};

async function getAuthSingleton(): Promise<Auth> {
  if (!authPromise) {
    authPromise = (async () => {
      const app = await initApp();
      return getAuth(app);
    })();
  }
  return authPromise;
}

export const setupAuthStateListener = async (
  onAuthStateChange: (user: FirebaseAuthUser | null) => void
): Promise<() => void> => {
  const auth = await getAuthSingleton();
  console.log("[firebase] attaching onAuthStateChanged");
  const unsubscribe = onAuthStateChanged(auth, (user) => {
    console.log("[firebase] auth state:", user ? "logged in" : "logged out");
    onAuthStateChange(user);
  });
  return unsubscribe;
};

export const signOutUser = async () => {
  await auth.signOut();
  // Also clear your API auth header
  delete api.defaults.headers.common.Authorization;
};
